import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import type { TFunction } from 'i18next';
import MobilePageHeader from '../../../shared/mobile/MobilePageHeader';
import { ResponsiveTable, MobileCardField } from '../../../shared/mobile/ResponsiveTable';
import RestaurantSelector from '../../../common/RestaurantSelector';
import { useAnalysisStore } from '../../../../stores/appAnalysisStore';
import type { WasteFinding, WasteActionItem } from '../../../../utils/analysis/wasteActionPayload';
import { eur } from '../../../orderhistory/utils';
import { formatCategoryName } from '../../../orderhistory/sections/FilterControls';
import { cn } from '../../../../lib/utils';
import {
  borderGrey, cardClass, darkGreenTextColor, darkOrangeTextColor, elevatedBg,
  primaryText, secondaryText, semiBoldText, tertiaryText,
} from '../../../../styling/stylingHelper';
import {
  AnalysisDetailHeader, AnalysisSearch, RankedGroupRow, WASTE_BADGE_CLASS, matchesFoodDrinks,
} from '../shared/AnalysisControls';
import { useAnalysisSelection } from '../shared/useAnalysisSelection';
import { fmtBaseQty } from '../shared/analysisUnits';

const PAGE_SIZE = 30;
const differenceLabel = (t: TFunction, quantity: number | null, unit: string | null, amount: number | null) => {
  const money = amount === null ? '—' : eur(amount);
  return quantity !== null && unit !== null
    ? `${quantity > 0 ? '+' : ''}${fmtBaseQty(t, quantity, unit)} · ${money}` : money;
};

/** Only reviewed, in-memory findings for the selected restaurant. */
const WastePage: React.FC = () => {
  const { isSuperAdmin, restaurantSelection } = useAnalysisSelection();
  const markSeen = useAnalysisStore(s => s.markAnalysisFindingsSeen);
  useEffect(() => { markSeen(); }, [markSeen]);
  if (!isSuperAdmin) return <Navigate to="/analysis" replace />;
  return <WastePageBody key={restaurantSelection} />;
};

const WastePageBody: React.FC = () => {
  const { t } = useTranslation();
  const { restaurantSelection, isAllRestaurants, isSuperAdmin } = useAnalysisSelection();
  const filter = useAnalysisStore(s => s.foodDrinksFilter);
  const findings = useAnalysisStore(s => s.wasteFindings[restaurantSelection]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (isAllRestaurants ? [] : findings ?? [])
      .filter(g => matchesFoodDrinks(filter, g.side))
      .filter(g => !q || g.groupName.toLowerCase().includes(q) ||
        g.members.some(m => m.name.toLowerCase().includes(q) || m.supplier.toLowerCase().includes(q)))
      .sort((a, b) => b.wasteEur - a.wasteEur);
  }, [findings, isAllRestaurants, filter, search]);
  const total = groups.reduce((sum, group) => sum + Math.max(0, group.wasteEur), 0);

  const toggleExpanded = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div>
      <MobilePageHeader title={t('analysis.waste.title')} right={<RestaurantSelector />} />
      <div className="space-y-4">
        <AnalysisDetailHeader
          icon={<Trash2 className={cn('w-5 h-5', darkOrangeTextColor)} />}
          title={t('analysis.waste.title')}
          subtitle={t('analysis.waste.formula')}
          totalLabel={t('analysis.waste.cardValueLabel')}
          totalValue={eur(total)}
          totalClassName={darkOrangeTextColor}
          showWindow={false}
        />
        <AnalysisSearch value={search} onChange={setSearch} placeholder={t('analysis.searchPlaceholder')} />
        {groups.length === 0 ? (
          <div className={cn(cardClass, 'p-8 text-center text-sm', secondaryText)}>{t('analysis.waste.empty')}</div>
        ) : (
          <div className="space-y-2">
            {groups.slice(0, visibleCount).map((group, i) => (
              <WasteGroupRow key={group.groupId} rank={i + 1} group={group} editable={isSuperAdmin}
                expanded={expanded.has(group.groupId)} onToggleExpanded={() => toggleExpanded(group.groupId)} />
            ))}
            {groups.length > visibleCount && (
              <button type="button" onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                className={cn('w-full py-2 text-sm rounded-lg border', borderGrey, secondaryText)}>
                {t('analysis.loadMore')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface DishTotal { dishName: string; unit: string; neededBase: number }

/** Expected usage per dish summed over the group's products (one line per dish + unit). */
function dishTotals(group: WasteFinding): DishTotal[] {
  const byDish = new Map<string, DishTotal>();
  for (const member of group.members) {
    for (const dish of member.dishes) {
      const id = `${dish.dishName}\u0000${member.stdUnit}`;
      const total = byDish.get(id) ?? { dishName: dish.dishName, unit: member.stdUnit, neededBase: 0 };
      total.neededBase += dish.neededBase;
      byDish.set(id, total);
    }
  }
  return Array.from(byDish.values()).filter(d => d.neededBase > 0).sort((a, b) => b.neededBase - a.neededBase);
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className={cn('text-xs uppercase tracking-wide', semiBoldText, secondaryText)}>{children}</h3>
);

const UsedInDishes: React.FC<{ group: WasteFinding }> = ({ group }) => {
  const { t } = useTranslation();
  const totals = useMemo(() => dishTotals(group), [group]);
  const max = totals.reduce((m, d) => Math.max(m, d.neededBase), 0);
  return (
    <section className={cn('rounded-xl border p-4 space-y-3', borderGrey)}>
      <SectionTitle>{t('analysis.waste.usedInDishes')}</SectionTitle>
      {totals.length === 0 ? <p className={cn('text-sm', secondaryText)}>{t('analysis.waste.noDishes')}</p> : (
        <ul className="space-y-3">
          {totals.map(d => (
            <li key={`${d.dishName}-${d.unit}`}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className={cn('min-w-0 break-words', primaryText)}>{d.dishName}</span>
                <span className={cn('whitespace-nowrap', semiBoldText, primaryText)}>{fmtBaseQty(t, d.neededBase, d.unit)}</span>
              </div>
              <div className={cn('mt-1 h-1.5 rounded-full', elevatedBg)}>
                <div className="h-1.5 rounded-full bg-darkGreenColor dark:bg-darkGreenColor-dark" style={{ width: `${max > 0 ? Math.max(2, (d.neededBase / max) * 100) : 0}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const textareaClass = cn('w-full min-w-0 rounded-lg border bg-background dark:bg-background-dark px-3 py-2 text-sm', borderGrey, primaryText);

/** Numbered action items; a super admin can edit, remove and add (text only — numbers stay as saved). */
const ActionItemsCard: React.FC<{ group: WasteFinding; editable: boolean }> = ({ group, editable }) => {
  const { t } = useTranslation();
  const setActionItems = useAnalysisStore(s => s.setWasteFindingActionItems);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const items = group.actionItems;
  const save = (next: WasteActionItem[]) => setActionItems(group.restaurantId, group.groupId, next);

  const startEdit = (item: WasteActionItem) => { setEditingId(item.id); setDraft(item.text); };
  const commitEdit = () => {
    const text = draft.trim();
    save(text
      ? group.actionItems.map(i => i.id === editingId ? { ...i, text } : i)
      : group.actionItems.filter(i => i.id !== editingId));
    setEditingId(null);
  };
  const cancelEdit = () => {
    // A freshly added item that was never given text disappears again.
    save(group.actionItems.filter(i => i.id !== editingId || i.text.trim()));
    setEditingId(null);
  };
  const add = () => {
    const item = { id: crypto.randomUUID(), text: '' };
    save([...group.actionItems, item]);
    startEdit(item);
  };

  return (
    <section className={cn('rounded-xl border p-4 space-y-3', borderGrey)}>
      <SectionTitle>{t('analysis.waste.actionsHeading')}</SectionTitle>
      {items.length === 0 && <p className={cn('text-sm', secondaryText)}>{t('analysis.waste.noActions')}</p>}
      <ol className="space-y-3">
        {items.map((item, index) => (
          <li key={item.id} className={cn('flex gap-3 border-b pb-3 last:border-b-0 last:pb-0', borderGrey)}>
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-darkGreenColor dark:bg-darkGreenColor-dark text-xs font-semibold text-white dark:text-white">{index + 1}</span>
            <div className="min-w-0 flex-1">
              {editingId === item.id ? (
                <div className="space-y-2">
                  <textarea aria-label={t('analysis.waste.actionItem')} value={draft} maxLength={2000} rows={2} autoFocus
                    onChange={e => setDraft(e.target.value)} className={textareaClass} />
                  <div className="flex gap-2">
                    <button type="button" onClick={commitEdit} className={cn('min-h-11 rounded-lg px-3 text-sm bg-darkGreenColor dark:bg-darkGreenColor-dark text-white dark:text-white', semiBoldText)}>{t('analysis.waste.saveItem')}</button>
                    <button type="button" onClick={cancelEdit} className={cn('min-h-11 rounded-lg border px-3 text-sm', borderGrey, primaryText)}>{t('analysis.waste.cancelEdit')}</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <p className={cn('min-w-0 flex-1 text-sm whitespace-pre-wrap break-words', primaryText)}>{item.text}</p>
                  {editable && (
                    <div className="flex flex-shrink-0">
                      <button type="button" aria-label={t('analysis.waste.editItem')} onClick={() => startEdit(item)} disabled={editingId !== null}
                        className={cn('flex h-11 w-11 items-center justify-center rounded-lg hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark disabled:opacity-40', secondaryText)}><Pencil className="h-4 w-4" /></button>
                      <button type="button" aria-label={t('analysis.waste.removeItem')} onClick={() => save(group.actionItems.filter(i => i.id !== item.id))} disabled={editingId !== null}
                        className={cn('flex h-11 w-11 items-center justify-center rounded-lg hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark disabled:opacity-40', secondaryText)}><X className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
      {editable && editingId === null && (
        <button type="button" onClick={add} className={cn('inline-flex min-h-11 items-center gap-2 text-sm', semiBoldText, darkGreenTextColor)}>
          <Plus className="h-4 w-4" />{t('analysis.waste.addItem')}
        </button>
      )}
    </section>
  );
};

interface WasteGroupRowProps {
  rank: number;
  group: WasteFinding;
  expanded: boolean;
  editable: boolean;
  onToggleExpanded: () => void;
}

const WasteGroupRow: React.FC<WasteGroupRowProps> = ({ rank, group, expanded, editable, onToggleExpanded }) => {
  const { t } = useTranslation();
  const meta = [group.category ? formatCategoryName(group.category) : '',
    t('analysis.productsCount', { count: group.members.length }), group.period.label].filter(Boolean).join(' · ');
  const totalQty = (quantity: number | null) => quantity === null || group.stdUnit === null ? '—' : fmtBaseQty(t, quantity, group.stdUnit);
  const wastePct = group.orderedBase && group.wasteBase !== null ? (group.wasteBase / group.orderedBase) * 100 : null;

  return (
    <RankedGroupRow rank={rank} title={group.groupName} meta={meta}
      badge={differenceLabel(t, group.wasteBase, group.stdUnit, group.wasteEur)} badgeClassName={WASTE_BADGE_CLASS}
      expanded={expanded} onToggleExpanded={onToggleExpanded}>
      {wastePct !== null && (
        <p className={cn('mt-3 text-sm', secondaryText)}>
          {t('analysis.waste.wastePct')}: <span className={cn(semiBoldText, darkOrangeTextColor)}>{wastePct.toFixed(1)}%</span>
        </p>
      )}
      <ResponsiveTable
        desktop={
          <table className="w-full text-sm mt-2">
            <thead><tr className={cn('text-left text-xs', tertiaryText)}>
              <th className="py-2 font-medium">{t('analysis.col.product')}</th>
              <th className="py-2 font-medium text-right">{t('analysis.col.bought')}</th>
              <th className="py-2 font-medium text-right">{t('analysis.col.expected')}</th>
              <th className="py-2 font-medium text-right">{t('analysis.col.difference')}</th>
            </tr></thead>
            <tbody>
              {group.members.map(member => (
                <tr key={member.key} className={cn('border-t', borderGrey)}>
                  <td className="py-2 pr-2"><p className={cn(semiBoldText, primaryText)}>{member.name}</p><p className={cn('text-xs', secondaryText)}>{member.supplier}</p></td>
                  <td className={cn('py-2 text-right', primaryText)}>{fmtBaseQty(t, member.orderedBase, member.stdUnit)}</td>
                  <td className={cn('py-2 text-right', primaryText)}>{fmtBaseQty(t, member.neededBase, member.stdUnit)}</td>
                  <td className={cn('py-2 text-right', semiBoldText, darkOrangeTextColor)}>{differenceLabel(t, member.wasteBase, member.stdUnit, member.wasteEur)}</td>
                </tr>
              ))}
              <tr className={cn('border-t', borderGrey)}>
                <td className={cn('py-2', semiBoldText, primaryText)}>{t('analysis.total')}</td>
                <td className={cn('py-2 text-right', semiBoldText, primaryText)}>{totalQty(group.orderedBase)}</td>
                <td className={cn('py-2 text-right', semiBoldText, primaryText)}>{totalQty(group.neededBase)}</td>
                <td className={cn('py-2 text-right', semiBoldText, darkOrangeTextColor)}>{differenceLabel(t, group.wasteBase, group.stdUnit, group.wasteEur)}</td>
              </tr>
            </tbody>
          </table>
        }
        mobile={
          <div className="space-y-2 mt-2">
            {group.members.map(member => (
              <div key={member.key} className={cn('rounded-lg border p-3', borderGrey)}>
                <p className={cn('text-sm', semiBoldText, primaryText)}>{member.name}</p>
                <p className={cn('text-xs', secondaryText)}>{member.supplier}</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <MobileCardField label={t('analysis.col.bought')} value={fmtBaseQty(t, member.orderedBase, member.stdUnit)} />
                  <MobileCardField label={t('analysis.col.expected')} value={fmtBaseQty(t, member.neededBase, member.stdUnit)} />
                  <MobileCardField className="col-span-2" label={t('analysis.col.difference')} value={differenceLabel(t, member.wasteBase, member.stdUnit, member.wasteEur)} />
                </div>
              </div>
            ))}
            <div className={cn('rounded-lg border p-3', borderGrey)}>
              <p className={cn('text-sm', semiBoldText, primaryText)}>{t('analysis.total')}</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <MobileCardField label={t('analysis.col.bought')} value={totalQty(group.orderedBase)} />
                <MobileCardField label={t('analysis.col.expected')} value={totalQty(group.neededBase)} />
                <MobileCardField className="col-span-2" label={t('analysis.col.difference')} value={differenceLabel(t, group.wasteBase, group.stdUnit, group.wasteEur)} />
              </div>
            </div>
          </div>
        }
      />
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <UsedInDishes group={group} />
        <ActionItemsCard group={group} editable={editable} />
      </div>
    </RankedGroupRow>
  );
};

export default WastePage;
